import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDeviceAuth } from '../context/DeviceAuthContext';
import '../styles/HomePage.css';
import videoConfig, { loadVideoConfig } from '../config/videos';
import imageConfig, { loadImageConfig } from '../config/images';

// Create a utility to help with debugging
const debugVideo = {
  log: (message: string, data?: any) => {
    console.log(`[VIDEO DEBUG] ${message}`, data || '');
  },
  error: (message: string, error?: any) => {
    console.error(`[VIDEO ERROR] ${message}`, error || '');
  },
  warn: (message: string, data?: any) => {
    console.warn(`[VIDEO WARN] ${message}`, data || '');
  }
};

// Simple Video Component with Fallback Strategy
const VideoPlayer: React.FC<{ onError: () => void }> = ({ onError }) => {
  const [useIframe, setUseIframe] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeUrl = `/video-player.html`;
  const videoUrl = `/assets/promo.mp4`;
  
  useEffect(() => {
    debugVideo.log("VideoPlayer mounted, useIframe:", useIframe);
    
    // If not using iframe, set up the video element
    if (!useIframe && videoRef.current) {
      const video = videoRef.current;
      
      // When video fails, try with iframe approach
      const handleError = () => {
        debugVideo.warn("Video tag failed, switching to iframe approach");
        setUseIframe(true);
      };
      
      // Add event listeners
      video.addEventListener('error', handleError);
      
      // Attempt to play
      video.addEventListener('canplaythrough', () => {
        debugVideo.log("Video can play through, attempting playback");
        video.play().catch(e => {
          debugVideo.error("Could not autoplay video:", e);
          video.muted = true;
          video.play().catch(err => {
            debugVideo.error("Still could not play even when muted:", err);
            handleError();
          });
        });
      }, { once: true });
      
      return () => {
        video.removeEventListener('error', handleError);
        video.removeEventListener('canplaythrough', () => {});
      };
    }
  }, [useIframe]);
  
  if (useIframe) {
    debugVideo.log("Rendering iframe player");
    return (
      <iframe 
        src={iframeUrl} 
        style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          border: 'none',
          objectFit: 'cover',
          zIndex: 1
        }}
        title="Promo Video"
        allow="autoplay; fullscreen"
        onError={onError}
      />
    );
  }
  
  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={`${process.env.PUBLIC_URL || ''}${videoConfig.posterImage}`}
      onError={(e) => {
        console.error('Video error:', e);
        setUseIframe(true);
      }}
      style={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        objectFit: 'cover',
        opacity: useIframe ? 0 : 1 
      }}
    >
      <source src={videoConfig.promoVideo} type="video/mp4" />
      Your browser does not support video playback.
    </video>
  );
};

interface HomePageProps {
  isStaging?: boolean;
}

const HomePage: React.FC<HomePageProps> = ({ isStaging = false }) => {
  const history = useHistory();
  const [showContent, setShowContent] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  
  // Use the device auth context to check authorization
  const { isDeviceAuthorized, loading } = useDeviceAuth();
  
  useEffect(() => {
    // Load configurations from the database
    const loadConfig = async () => {
      try {
        // Load both video and image configurations
        await Promise.all([
          loadVideoConfig(),
          loadImageConfig()
        ]);
        
        setConfigLoaded(true);
        
        // Log loaded configurations
        debugVideo.log('Configurations loaded:', {
          promoVideo: videoConfig.promoVideo,
          posterImage: videoConfig.posterImage,
          backgroundImage: imageConfig.backgroundImage,
          watermarkImage: imageConfig.watermarkImage
        });
      } catch (err) {
        console.error('Failed to load configurations:', err);
        setConfigLoaded(true); // Continue with default values
      }
    };
    
    // Load the configurations
    loadConfig();
    
    // Clear all localStorage and sessionStorage data
    const clearStorages = () => {
      try {
        // Save important items that should persist
        const deviceId = localStorage.getItem('deviceId');
        const userEmail = localStorage.getItem('userEmail');
        const hasSeenPromo = localStorage.getItem('hasSeenPromo');
        
        // Save authentication-related items
        const authToken = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        const testAdminToken = localStorage.getItem('test-admin-token');
        
        // Save admin authentication data
        const adminUser = localStorage.getItem('adminUser');
        
        // Clear storages
        localStorage.clear();
        sessionStorage.clear();
        
        console.log('Cleared localStorage and sessionStorage');
        
        // Restore essential items
        if (deviceId) localStorage.setItem('deviceId', deviceId);
        if (userEmail) localStorage.setItem('userEmail', userEmail);
        if (hasSeenPromo) localStorage.setItem('hasSeenPromo', hasSeenPromo);
        
        // Restore authentication items
        if (authToken) localStorage.setItem('token', authToken);
        if (userData) localStorage.setItem('user', userData);
        if (testAdminToken) localStorage.setItem('test-admin-token', testAdminToken);
        
        // Restore admin authentication data
        if (adminUser) {
          localStorage.setItem('adminUser', adminUser);
          console.log('Preserved admin authentication data');
        }
      } catch (err) {
        console.error('Error clearing storage:', err);
      }
    };
    
    // Clear the storages
    clearStorages();
    
    // Show content after a short delay
    setTimeout(() => {
      setShowContent(true);
    }, 500);

    // Log environment info
    debugVideo.log('Environment info', {
      publicUrl: process.env.PUBLIC_URL,
      nodeEnv: process.env.NODE_ENV,
      configUrl: videoConfig.promoVideo,
      window: window.location.href
    });
  }, []);
  
  const handleButtonClick = () => {
    // Use the appropriate path based on isStaging prop
    const basePath = isStaging ? '/staging' : '/box';
    history.push(`${basePath}/video-selection`);
  };
  
  const handleVideoError = () => {
    debugVideo.error("Final video error handler called, showing static background");
    setVideoError(true);
  };
  
  return (
    <div className="home-container">
      <div className="video-background">
        {configLoaded && !videoError && <VideoPlayer onError={handleVideoError} />}
      </div>
      
      {videoError && (
        <div 
          className="fallback-background" 
          style={{ 
            backgroundImage: `url(${process.env.PUBLIC_URL || ''}${videoConfig.posterImage})` 
          }} 
        />
      )}
      
      <div className="content-overlay">
        <div className={`logo-container ${showContent ? 'visible' : ''}`}>
          <img 
            src={`${process.env.PUBLIC_URL || ''}/assets/logo-white.jpg`}
            alt="Clipo Logo" 
            className="logo-image"
            onError={(e) => debugVideo.error("Logo loading error:", e)}
          />
        </div>
        
        {/* Only show the button if device is authorized and not in loading state */}
        {!loading && isDeviceAuthorized && (
          <button 
            className={`cta-button ${showContent ? 'visible' : ''}`}
            onClick={handleButtonClick}
          >
            See Your Clipo
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default HomePage;